import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { planApi } from "../../data/api/PlanApi";
import { Plan } from "../../data/models/Plan/PlanModel";
import { SoulDataModel } from "../../data/models/Soul/SoulDataModel";

interface Props {
  open: boolean;
  onClose: () => void;
  users: SoulDataModel[];
  plans: Plan[];
}

export function CreateEmailPlanPreviewModal({
  open,
  onClose,
  users,
  plans,
}: Props) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [planCode, setPlanCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const emailOptions = useMemo(() => {
    return Array.from(
      new Set(
        users
          .map((u) => u.email?.trim().toLowerCase())
          .filter((value): value is string => Boolean(value)),
      ),
    );
  }, [users]);

  const selectedUser = useMemo(() => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return undefined;
    return users.find((u) => u.email?.trim().toLowerCase() === normalizedEmail);
  }, [email, users]);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setFullName("");
      setPlanCode("");
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  useEffect(() => {
    if (selectedUser?.fullName && !fullName.trim()) {
      setFullName(selectedUser.fullName);
    }
  }, [fullName, selectedUser]);

  const handleSubmit = async () => {
    if (!email || !planCode || !fullName) {
      setError("Todos los datos son obligatorios.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Correo inválido.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const result = await planApi.postEmailPreview.request({
        email: email.trim().toLowerCase(),
        fullName: fullName?.trim(),
        planCode,
      });
      if (!result.isSuccess) {
        throw new Error(result.messageError || "No se pudo asignar el plan.");
      }
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1200);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Ocurrió un error inesperado.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Asignar Plan por Email</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Plan asignado correctamente
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          <Autocomplete
            freeSolo
            options={emailOptions}
            value={email}
            onChange={(_, value) => setEmail(value || "")}
            onInputChange={(_, value) => setEmail(value)}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => <TextField {...params} label="Correo" />}
          />
          <TextField
            label="Nombre completo"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <TextField
            select
            label="Seleccionar Plan"
            value={planCode}
            onChange={(e) => setPlanCode(e.target.value)}
          >
            {(plans ?? []).map((plan) => (
              <MenuItem key={plan.code} value={plan.code}>
                {plan.name}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            El usuario podrá activar este plan automáticamente al registrarse.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={loading || !plans?.length}
          onClick={handleSubmit}
        >
          {loading ? "Creando..." : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
